import { useState, useEffect } from "react";
import { Routes, Route, Navigate, useNavigate } from "react-router-dom";
import { fetchJsonCached, invalidateApiCache } from "../api/cache";
import { useAuth } from "../providers/auth-provider";
import { LoginPage } from "../../features/auth/pages/login-page";
import { AppLayout } from "../../components/layout/app-layout";
import { FilesPanel } from "../../features/file-manager/components/files-panel";
import { KanbanPanel } from "../../features/kanban/components/kanban-panel";
import { TodosPanel } from "../../features/todos/components/todos-panel";
import { NotesPanel } from "../../features/notes/components/notes-panel";
import { SettingsView } from "../../features/settings/settings-view";
import {
  Cloud,
  CheckCircle2,
  File,
  FileImage,
  FileText,
  Kanban,
  FileEdit,
  Plus,
  Check,
} from "lucide-react";

// ── Dashboard widgets ──────────────────────────────────────────────────────

function TasksWidget() {
  const navigate = useNavigate();
  const [todos, setTodos] = useState<any[]>([]);

  useEffect(() => {
    fetchJsonCached<any[]>("/api/todos")
      .then(setTodos)
      .catch(() => {});
  }, []);

  const toggle = async (todo: any) => {
    const next = todo.status === "completed" ? "pending" : "completed";
    await fetch(`/api/todos/${todo.id}`, {
      method: "PATCH",
      credentials: "include",
      headers: { "Content-Type": "application/json" },
      body: JSON.stringify({ status: next }),
    });
    invalidateApiCache("/api/todos");
    setTodos((prev) =>
      prev.map((t) => (t.id === todo.id ? { ...t, status: next } : t)),
    );
  };

  return (
    <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-5 flex flex-col h-full">
      <div className="flex items-center gap-2 mb-4">
        <CheckCircle2 size={18} className="text-on-surface-variant" />
        <span className="text-h3 font-semibold">Tasks</span>
      </div>
      <div className="flex-1 overflow-y-auto space-y-1 divide-y divide-outline-variant/50">
        {todos.length === 0 && (
          <p className="text-body-md text-on-surface-variant py-4 text-center">
            No tasks yet
          </p>
        )}
        {todos.map((t) => (
          <div
            key={t.id}
            className="flex items-center gap-3 py-2.5 cursor-pointer group"
            onClick={() => toggle(t)}
          >
            <div
              className={`w-4 h-4 rounded border flex items-center justify-center shrink-0 transition-colors ${
                t.status === "completed"
                  ? "bg-primary border-primary"
                  : "border-outline-variant group-hover:border-primary"
              }`}
            >
              {t.status === "completed" && (
                <Check size={10} className="text-white" />
              )}
            </div>
            <span
              className={`text-body-md ${t.status === "completed" ? "line-through text-on-surface-variant" : "text-on-surface"}`}
            >
              {t.title}
            </span>
          </div>
        ))}
      </div>
      <button
        onClick={() => navigate("/todos")}
        className="mt-4 flex items-center gap-1.5 text-body-md text-on-surface-variant hover:text-primary transition-colors"
      >
        <Plus size={14} />
        Add task
      </button>
    </div>
  );
}

function ProjectsWidget() {
  const navigate = useNavigate();
  const [columns, setColumns] = useState<any[]>([]);
  const [cards, setCards] = useState<Record<string, any[]>>({});

  useEffect(() => {
    fetchJsonCached<any[]>("/api/kanban/boards")
      .then(async (boards: any[]) => {
        if (!boards.length) return;
        const board = boards[0];
        const cols = await fetchJsonCached<any[]>(
          `/api/kanban/boards/${board.id}/columns`,
        );
        setColumns(cols.slice(0, 3));
        const cardMap: Record<string, any[]> = {};
        await Promise.all(
          cols.slice(0, 3).map(async (col) => {
            cardMap[col.id] = await fetchJsonCached<any[]>(
              `/api/kanban/columns/${col.id}/cards`,
            );
          }),
        );
        setCards(cardMap);
      })
      .catch(() => {});
  }, []);

  return (
    <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-5">
      <div className="flex items-center gap-2 mb-4">
        <Kanban size={18} className="text-on-surface-variant" />
        <span className="text-h3 font-semibold">Projects</span>
      </div>
      {columns.length === 0 ? (
        <p className="text-body-md text-on-surface-variant">No boards yet</p>
      ) : (
        <div className="flex flex-col sm:flex-row gap-3 overflow-x-hidden sm:overflow-x-auto pb-1">
          {columns.map((col) => (
            <div key={col.id} className="min-w-0 sm:min-w-[140px] flex-1">
              <p className="text-xs font-semibold tracking-wider uppercase text-on-surface-variant mb-2">
                {col.title}
              </p>
              <div className="space-y-1.5">
                {(cards[col.id] ?? []).slice(0, 3).map((card) => (
                  <div
                    key={card.id}
                    className="bg-surface-container-low rounded-lg px-3 py-2 text-body-md border border-outline-variant/60"
                  >
                    {card.title}
                  </div>
                ))}
                {(cards[col.id] ?? []).length === 0 && (
                  <div className="text-xs text-on-surface-variant/60 italic">
                    Empty
                  </div>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => navigate("/kanban")}
        className="mt-3 text-xs text-on-surface-variant hover:text-primary transition-colors"
      >
        Open board →
      </button>
    </div>
  );
}

function DriveWidget() {
  const navigate = useNavigate();
  const [files, setFiles] = useState<any[]>([]);

  useEffect(() => {
    fetchJsonCached<any[]>("/api/files")
      .then((data: any[]) => setFiles(data.slice(0, 4)))
      .catch(() => {});
  }, []);

  function fileIcon(mime: string) {
    if (mime?.startsWith("image/")) return FileImage;
    if (mime?.startsWith("text/") || mime === "application/pdf") return FileText;
    return File;
  }

  function relTime(iso: string) {
    const diff = Date.now() - new Date(iso).getTime();
    const h = Math.floor(diff / 3600000);
    if (h < 1) return "Just now";
    if (h < 24) return `${h}h ago`;
    const d = Math.floor(diff / 86400000);
    return d === 1 ? "Yesterday" : `${d}d ago`;
  }

  return (
    <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-5">
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          <Cloud size={18} className="text-on-surface-variant" />
          <span className="text-h3 font-semibold">Drive</span>
        </div>
        <button
          onClick={() => navigate("/files")}
          className="text-xs text-on-surface-variant hover:text-primary transition-colors"
        >
          View all
        </button>
      </div>
      {files.length === 0 ? (
        <p className="text-body-md text-on-surface-variant">No files yet</p>
      ) : (
        <div className="space-y-2">
          {files.map((f) => {
            const Icon = fileIcon(f.mimeType ?? f.type ?? "");
            return (
              <div key={f.id} className="flex items-center gap-3">
                <div className="w-8 h-8 rounded-lg bg-surface-container flex items-center justify-center shrink-0">
                  <Icon size={15} className="text-on-surface-variant" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="text-body-md truncate">{f.filename}</p>
                  <p className="text-xs text-on-surface-variant">
                    Modified {relTime(f.updatedAt ?? f.createdAt)}
                  </p>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}

function NotesWidget() {
  const navigate = useNavigate();
  const [notes, setNotes] = useState<any[]>([]);

  useEffect(() => {
    fetchJsonCached<any[]>("/api/notes")
      .then((data: any[]) => setNotes(data.slice(0, 3)))
      .catch(() => {});
  }, []);

  return (
    <div className="bg-surface-container-lowest rounded-xl border border-outline-variant p-5">
      <div className="flex items-center gap-2 mb-4">
        <FileEdit size={18} className="text-on-surface-variant" />
        <span className="text-h3 font-semibold">Notes</span>
      </div>
      {notes.length === 0 ? (
        <p className="text-body-md text-on-surface-variant italic">
          Jot down quick thoughts…
        </p>
      ) : (
        <div className="space-y-2">
          {notes.map((n) => (
            <div
              key={n.id}
              onClick={() => navigate("/notes")}
              className="cursor-pointer"
            >
              <p className="text-body-md font-medium truncate">{n.title}</p>
              {n.content && (
                <p className="text-xs text-on-surface-variant truncate">
                  {n.content.slice(0, 60)}
                </p>
              )}
            </div>
          ))}
        </div>
      )}
      <button
        onClick={() => navigate("/notes")}
        className="mt-3 text-xs text-on-surface-variant hover:text-primary transition-colors"
      >
        Open notes →
      </button>
    </div>
  );
}

function DashboardPage() {
  return (
    <div className="p-md sm:p-6 overflow-auto overflow-x-hidden h-full">
      <h1 className="text-h1 mb-1">Personal Workspace</h1>
      <p className="text-body-lg text-on-surface-variant mb-6">
        Overview of your daily operations across all functional hubs.
      </p>

      <div className="flex flex-col lg:flex-row gap-5 lg:h-[calc(100%-100px)]">
        {/* Left column — Tasks (full height) */}
        <div className="w-full lg:w-[48%] min-w-0">
          <TasksWidget />
        </div>

        {/* Right column — Projects + Drive + Notes */}
        <div className="flex-1 flex flex-col gap-5 min-w-0">
          <ProjectsWidget />
          <div className="flex flex-col md:flex-row gap-5 flex-1 min-h-0">
            <div className="flex-1 min-w-0">
              <DriveWidget />
            </div>
            <div className="flex-1 min-w-0">
              <NotesWidget />
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DriveView() {
  return <FilesPanel />;
}

function KanbanView() {
  return <KanbanPanel />;
}

function TasksView() {
  return <TodosPanel />;
}

function NotesView() {
  return <NotesPanel />;
}

// ── Protected route guard ──

function ProtectedRoute({ children }: { children: React.ReactNode }) {
  const { isAuthenticated, isLoading } = useAuth();

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-screen bg-surface">
        <p className="text-body-lg text-on-surface-variant">Loading…</p>
      </div>
    );
  }

  if (!isAuthenticated) {
    return <Navigate to="/login" replace />;
  }

  return <>{children}</>;
}

// ── Router ──

export function AppRouter() {
  return (
    <Routes>
      <Route path="/login" element={<LoginPage />} />
      <Route
        path="/"
        element={
          <ProtectedRoute>
            <AppLayout />
          </ProtectedRoute>
        }
      >
        <Route index element={<DashboardPage />} />
        <Route path="files" element={<DriveView />} />
        <Route path="kanban" element={<KanbanView />} />
        <Route path="todos" element={<TasksView />} />
        <Route path="notes" element={<NotesView />} />
        <Route path="settings" element={<SettingsView />} />
      </Route>
      <Route path="*" element={<Navigate to="/" replace />} />
    </Routes>
  );
}
